import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { MedplumClient } from "@medplum/core";

import * as Sentry from "@sentry/react";

import {
  LABOR_AMEND_BIRTH_LOAD_FAILED_MESSAGE,
  LABOR_AMEND_BIRTH_LOAD_FAILED_TITLE,
} from "@/encounters/components/EncounterLabor/constants/labor-encounter-amend";
import { fetchLaborStageObservations } from "@/encounters/components/EncounterLabor/services/labor";
import { fetchEncounterBirthObservations } from "@/encounters/components/EncounterLabor/utils/fetch-encounter-birth-observations";
import type {
  BirthObservationsLoadStatus,
  LaborStagesLoadStatus,
} from "@/encounters/components/EncounterLabor/utils/labor-amend-sign-and-lock-ready";
import { dedupeFinalLaborStageObservations } from "@/encounters/components/EncounterLabor/utils/labor-stage-dedupe";
import { clearBirthState, setBirthObservations } from "@/store/slices/birth";
import { loadLaborStages } from "@/store/slices/labor-stages";
import { toast } from "@/utils/notifications";

interface UseLaborAmendInitialDataParams {
  clientId?: string;
  encounterId?: string;
  medplum: MedplumClient;
}

interface UseLaborAmendInitialDataResult {
  laborStagesLoadStatus: LaborStagesLoadStatus;
  birthObservationsLoadStatus: BirthObservationsLoadStatus;
}

/**
 * Loads labor-stage and birth Observation prerequisites for the labor amend shell.
 */
export function useLaborAmendInitialData({
  clientId,
  encounterId,
  medplum,
}: UseLaborAmendInitialDataParams): UseLaborAmendInitialDataResult {
  const dispatch = useDispatch();
  const [laborStagesLoadStatus, setLaborStagesLoadStatus] =
    useState<LaborStagesLoadStatus>("loading");
  const [birthObservationsLoadStatus, setBirthObservationsLoadStatus] =
    useState<BirthObservationsLoadStatus>("loading");

  useEffect(() => {
    if (!clientId || !encounterId) {
      return;
    }

    let isCancelled = false;

    const loadLaborStagesForAmend = async () => {
      setLaborStagesLoadStatus("loading");
      dispatch(loadLaborStages([]));
      try {
        const laborStages = await fetchLaborStageObservations(medplum, clientId, encounterId);
        if (isCancelled) {
          return;
        }
        dispatch(loadLaborStages(dedupeFinalLaborStageObservations(laborStages)));
        setLaborStagesLoadStatus("ready");
      } catch (error) {
        if (isCancelled) {
          return;
        }
        dispatch(loadLaborStages([]));
        setLaborStagesLoadStatus("failed");
        Sentry.captureException(error, {
          tags: { laborAmendPrerequisite: "labor-stages" },
        });
      }
    };

    const loadBirthObservationsForAmend = async () => {
      setBirthObservationsLoadStatus("loading");
      dispatch(clearBirthState());
      try {
        const birthObservations = await fetchEncounterBirthObservations(
          medplum,
          clientId,
          encounterId,
        );

        if (isCancelled) {
          return;
        }
        if (birthObservations.length > 0) {
          dispatch(setBirthObservations(birthObservations));
        } else {
          dispatch(clearBirthState());
        }
        setBirthObservationsLoadStatus("ready");
      } catch (error) {
        if (!isCancelled) {
          dispatch(clearBirthState());
          setBirthObservationsLoadStatus("failed");
          Sentry.captureException(error, {
            tags: { laborAmendPrerequisite: "birth-observations" },
          });
          toast({
            title: LABOR_AMEND_BIRTH_LOAD_FAILED_TITLE,
            message: LABOR_AMEND_BIRTH_LOAD_FAILED_MESSAGE,
            type: "warning",
            autoClose: false,
          });
        }
      }
    };

    void loadLaborStagesForAmend();
    void loadBirthObservationsForAmend();

    return () => {
      isCancelled = true;
    };
  }, [clientId, encounterId, dispatch, medplum]);

  return { laborStagesLoadStatus, birthObservationsLoadStatus };
}
